import { initializeApp, type FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Database } from 'firebase/database';
import type { Functions } from 'firebase/functions';

const env = import.meta.env;

export const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: env.VITE_FIREBASE_DATABASE_URL,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
  measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
};

/** Online play needs a configured project; without one the app runs offline (practice only). */
export const firebaseEnabled = Boolean(
  firebaseConfig.apiKey &&
  firebaseConfig.projectId &&
  firebaseConfig.databaseURL &&
  firebaseConfig.appId,
);

export const useEmulators = env.VITE_USE_EMULATORS === 'true';
/**
 * Emulators are reached on the host the page was served from, so a phone that opened
 * http://192.168.1.6:5173 talks to the emulators on 192.168.1.6 (LAN testing).
 */
export const emulatorHost =
  env.VITE_EMULATOR_HOST || (typeof location !== 'undefined' ? location.hostname : '127.0.0.1');
export const functionsRegion = env.VITE_FUNCTIONS_REGION || 'asia-southeast1';

/**
 * Where the `api` callable lives when it is not a Cloud Function: the standalone game server
 * (apps/server), e.g. https://mendikot-api.example.org/api, or `/api` through the Vite dev proxy.
 * Empty means the `api` Cloud Function in `functionsRegion`.
 */
export const apiUrl = env.VITE_API_URL
  ? new URL(
      env.VITE_API_URL,
      typeof location !== 'undefined' ? location.origin : 'http://localhost',
    ).toString()
  : '';

export interface FirebaseCore {
  app: FirebaseApp;
}

export interface FirebaseOnline {
  db: Database;
  functions: Functions;
}

let core: FirebaseCore | null = null;
let authPromise: Promise<Auth> | null = null;
let online: Promise<FirebaseOnline> | null = null;

/** The Firebase app itself (tiny); every SDK on top of it is loaded on demand. */
export function getFirebase(): FirebaseCore | null {
  if (!firebaseEnabled) return null;
  core ??= { app: initializeApp(firebaseConfig) };
  return core;
}

/** App Check + Auth, loaded right after first paint for the guest identity. */
export function loadAuth(): Promise<Auth> {
  const base = getFirebase();
  if (!base) return Promise.reject(new Error('Firebase is not configured'));
  authPromise ??= (async () => {
    if (env.VITE_RECAPTCHA_ENTERPRISE_KEY) {
      const { initializeAppCheck, ReCaptchaEnterpriseProvider } =
        await import('firebase/app-check');
      if (env.VITE_APPCHECK_DEBUG === 'true') {
        (
          self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }
        ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      initializeAppCheck(base.app, {
        provider: new ReCaptchaEnterpriseProvider(env.VITE_RECAPTCHA_ENTERPRISE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    }
    const sdk = await import('firebase/auth');
    const auth = sdk.initializeAuth(base.app, {
      persistence: [sdk.indexedDBLocalPersistence, sdk.browserLocalPersistence],
      popupRedirectResolver: sdk.browserPopupRedirectResolver,
    });
    if (useEmulators)
      sdk.connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
    return auth;
  })();
  return authPromise;
}

/**
 * Realtime Database + Functions, loaded on first online use so practice-only sessions never pay
 * for them (plan §3 performance budget). The Functions instance also serves `httpsCallableFromURL`
 * calls to the standalone server, attaching the same Auth and App Check tokens.
 */
export function loadOnline(): Promise<FirebaseOnline> {
  const base = getFirebase();
  if (!base) return Promise.reject(new Error('Firebase is not configured'));
  online ??= Promise.all([
    loadAuth(),
    import('firebase/database'),
    import('firebase/functions'),
  ]).then(([, dbSdk, fnSdk]) => {
    const db = dbSdk.getDatabase(base.app);
    const functions = fnSdk.getFunctions(base.app, functionsRegion);
    if (useEmulators) {
      dbSdk.connectDatabaseEmulator(db, emulatorHost, 9000);
      if (!apiUrl) fnSdk.connectFunctionsEmulator(functions, emulatorHost, 5001);
    }
    return { db, functions };
  });
  return online;
}
